// ** MUI Imports
import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

// ** Custom Components Imports
import { buildingList } from 'src/data/building'
import CustomHeader from 'src/@core/components/Header'
import TablaReferencia from 'src/@core/components/table/TablaReferencia'
import { nFormatter } from 'src/@core/utils/numberFormatter'

/*
  Esta pagina usaba @mui/x-data-grid con pageSize 15. Dos consecuencias que se
  midieron sobre out/ y que valen mas que la comodidad del componente:

    - DataGrid es virtualizado: no emite ni una fila en el render del servidor.
      El HTML publicado tenia `<tr>` = 0 y `<td>` = 0, mientras el propio texto de
      la pagina anuncia "440 rows in total".
    - Aun con el JS ejecutado, la paginacion dejaba 15 filas de 440 a la vista, y
      las otras 425 no eran alcanzables por ninguna URL.

  TablaReferencia emite las 440 filas en el HTML y deja el filtro y el orden como
  mejora progresiva. Ademas esta pagina deja de cargar x-data-grid.

  Nota sobre nFormatter: la celda sigue mostrando 61.5M como antes, pero el numero
  exacto viaja en el atributo title, asi que el dato completo esta en el HTML.
*/
const columnas = [
  {
    clave: 'desc',
    encabezado: 'Item',
    ordenable: true,
    formato: (v, fila) => (
      <>
        {v}
        <Typography variant='caption' component='span' sx={{ display: 'block', fontWeight: 400 }}>
          {fila.type}
        </Typography>
      </>
    )
  },
  { clave: 'level', encabezado: 'Level', numerica: true, ordenable: true, ancho: 70 },
  {
    clave: 'azurite',
    encabezado: 'Azurite',
    numerica: true,
    ordenable: true,
    valor: f => f.rss?.a ?? 0,
    formato: v => nFormatter(v).toLocaleString()
  },
  {
    clave: 'food',
    encabezado: 'Food',
    numerica: true,
    ordenable: true,
    valor: f => f.rss?.f ?? 0,
    formato: v => nFormatter(v).toLocaleString()
  },
  {
    clave: 'wood',
    encabezado: 'Wood',
    numerica: true,
    ordenable: true,
    valor: f => f.rss?.w ?? 0,
    formato: v => nFormatter(v).toLocaleString()
  },
  {
    clave: 'stone',
    encabezado: 'Stone',
    numerica: true,
    ordenable: true,
    valor: f => f.rss?.s ?? 0,
    formato: v => nFormatter(v).toLocaleString()
  },
  {
    clave: 'iron',
    encabezado: 'Iron',
    numerica: true,
    ordenable: true,
    valor: f => f.rss?.i ?? 0,
    formato: v => nFormatter(v).toLocaleString()
  }
]

const Building = () => {
  return (
    <Grid container spacing={6}>
      <Grid size={{ xs: 12 }}>
        <Card
          sx={{
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            borderTop: '6px solid',
            borderTopColor: 'primary.main'
          }}
        >
          <CustomHeader icon='building' title='BUILDINGS LIST' />
          <Divider sx={{ marginY: '1rem' }} />
          <Typography
            variant='h6'
            component='h2'
            sx={{ fontWeight: 'bold', marginBottom: '1rem', color: 'primary.main' }}
          >
            Buildings List
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
            Browse the complete list of War and Order buildings with their resource costs at every upgrade level. Each
            entry shows the Azurite, Food, Wood, Stone, and Iron required to build or upgrade, making it easy to plan
            your construction queue before spending resources.
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Use the Data Filter to search by building name or type. Sort any column by clicking the column header.
            Combine this reference with the Building and Technology Planner for full upgrade planning.
          </Typography>
        </Card>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Card>
          <TablaReferencia
            columnas={columnas}
            filas={buildingList}
            idFila={f => `${f.building}-${f.level}`}
            titulo='War and Order building upgrade costs, every level'
            nota='hover a number to see the exact figure'
            clavesFiltro={['desc', 'type', 'building']}
          />
        </Card>
      </Grid>
    </Grid>
  )
}

Building.noLoadingGate = true

export default Building
